import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  findIndexesForSelection,
  findIndexOfHoveredNode,
  isPointerInsideContainer,
} from '../utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { useNavigate } from 'react-router-dom';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomSelectedDocumentsContext from '../../../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import { useTranslation } from 'react-i18next';
import DataRoomDocumentsContext from '../../../providers/DataRoomDocuments/DataRoomDocuments.context';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import {
  getDataRoomRoute,
  getIsNodeFile,
  getIsNodeFolder,
  isOpenDrawerOrModal,
} from 'utils';

const SCROLL_BUY = 30;

export default function useSelectAndDeselect() {
  const [isDragging, setIsDragging] = useState(false);
  const [isSelectioningOnDrag, setIsSelectioningOnDrag] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);

  const timeRef = useRef<NodeJS.Timeout | number>();
  const selectionOnDragStart = useRef<MouseEvent>(null);
  const topOffsetOnDragStart = useRef<number>(null);
  const docListRef = useRef<HTMLDivElement>(null);
  const draggableOverlayRef = useRef<HTMLDivElement>(null);
  const containersRefs = useRef([]);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { currentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);
  const { selected, setSelected } = useContext(
    DataRoomSelectedDocumentsContext,
  );
  const {
    role,
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onDragMove = useCallback(
    async (nodeToMoveTo: DataRoomNode) => {
      if (getIsNodeFile(nodeToMoveTo)) return;

      const filesIds = selected
        .filter(({ _id }) => _id !== nodeToMoveTo?._id)
        .map(({ _id }) => _id);

      if (!filesIds.length) return;

      try {
        setIsRequestPending(true);

        await dataRoomApi.nodes.moveFilesToFolder(filesIds, nodeToMoveTo._id);

        popSuccess(
          t('successfullyMovedFilesToThisFolder', {
            count: selected.length,
            folderName: nodeToMoveTo.name,
          }),
        );

        navigate(
          `${getDataRoomRoute(_id, 'documents')}/${nodeToMoveTo._id || ''}`,
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsRequestPending(false);
      }
    },
    [selected, popSuccess, t, navigate, _id, popServerError],
  );

  const selectOnDrag = useCallback(
    (
      selectionOnDragEnd: MouseEvent,
      rect: DOMRect,
      topScrollOffset: number,
    ) => {
      const indexes = findIndexesForSelection(
        selectionOnDragStart.current,
        selectionOnDragEnd,
        rect,
        topOffsetOnDragStart.current,
        topScrollOffset,
      );

      if (indexes && indexes.length && !isDataRoomViewer(role)) {
        setSelected(() =>
          currentlyDisplayedNodes.filter((_, index) => indexes.includes(index)),
        );
      }
    },
    [currentlyDisplayedNodes, role, setSelected],
  );

  const prepareDraggableStyle = useCallback(
    (e: MouseEvent) => {
      const { clientX, clientY } = e;

      const resetContainerStyles = () => {
        draggableOverlayRef?.current?.classList?.remove('accepted');

        containersRefs.current?.forEach((el) =>
          el?.classList?.remove('accepted-div-for-dropping'),
        );
      };

      const rect = docListRef.current?.getBoundingClientRect();
      const topScrollOffset = docListRef.current?.scrollTop;

      if (isSelectioningOnDrag) {
        selectOnDrag(e, rect, topScrollOffset);
      }

      if (isDragging && isPointerInsideContainer(e, rect)) {
        const nodeIndex = findIndexOfHoveredNode(e, rect, topScrollOffset);

        resetContainerStyles();

        const isDestinationFolder = getIsNodeFolder(
          currentlyDisplayedNodes?.[nodeIndex],
        );

        if (nodeIndex > -1 && isDestinationFolder) {
          draggableOverlayRef?.current?.classList?.add('accepted');
          containersRefs?.current?.[nodeIndex]?.classList?.add(
            'accepted-div-for-dropping',
          );
        }
      } else {
        resetContainerStyles();
      }

      draggableOverlayRef?.current?.setAttribute(
        'style',
        `top:${clientY}px; left:${clientX + 4}px;`,
      );
    },
    [currentlyDisplayedNodes, isDragging, isSelectioningOnDrag, selectOnDrag],
  );

  const resetInterval = () => {
    clearInterval(timeRef.current as number);
  };

  const handleScroll = useCallback((e: MouseEvent) => {
    resetInterval();

    timeRef.current = setInterval(() => {
      const { clientY } = e;
      const { top, height } = docListRef.current?.getBoundingClientRect() || {};

      let scrollBy = 0;

      if (clientY < top) {
        scrollBy = -SCROLL_BUY;
      }

      if (clientY > top + height) {
        scrollBy = SCROLL_BUY;
      }

      docListRef.current?.scrollBy({
        top: scrollBy,
        behavior: 'smooth',
      });
    }, 100);
  }, []);

  const onDrop = useCallback(
    (e: MouseEvent) => {
      if (isSelectioningOnDrag) {
        setIsSelectioningOnDrag(false);
      }

      if (isDragging) {
        const rect = docListRef.current?.getBoundingClientRect();

        if (isPointerInsideContainer(e, rect)) {
          const nodeIndex = findIndexOfHoveredNode(
            e,
            rect,
            docListRef.current?.scrollTop,
          );

          if (nodeIndex > -1) {
            onDragMove(currentlyDisplayedNodes?.[nodeIndex]);
          }
        }

        setIsDragging(false);
      }
    },
    [currentlyDisplayedNodes, isDragging, isSelectioningOnDrag, onDragMove],
  );

  const handleSelectOnDrag = useCallback(
    (e: MouseEvent) => {
      if (isDragging) return;

      const rect = docListRef.current?.getBoundingClientRect();
      const actionMenu = document.getElementsByClassName('action-menu')?.[0];

      const shouldSelect =
        !isPointerInsideContainer(e, rect) &&
        !isPointerInsideContainer(e, actionMenu?.getBoundingClientRect());

      if (shouldSelect) {
        setIsSelectioningOnDrag(true);

        selectionOnDragStart.current = e;
        topOffsetOnDragStart.current = docListRef.current?.scrollTop;
      }
    },
    [isDragging],
  );

  const handleDeselect = useCallback(
    (e: MouseEvent) => {
      const rect = docListRef.current?.getBoundingClientRect();
      const actionMenu = document.getElementsByClassName('action-menu')?.[0];

      if (
        !isOpenDrawerOrModal() &&
        !isPointerInsideContainer(e, rect) &&
        !isPointerInsideContainer(e, actionMenu?.getBoundingClientRect())
      ) {
        setSelected([]);
      }
    },
    [setSelected],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleSelectOnDrag);
    document.addEventListener('mousemove', prepareDraggableStyle);
    document.addEventListener('mouseup', onDrop);

    if (!isDragging && !isSelectioningOnDrag) {
      document.addEventListener('mousedown', handleDeselect);
    } else {
      document.removeEventListener('mousedown', handleDeselect);
    }

    if (isDragging || isSelectioningOnDrag) {
      document.addEventListener('mouseover', handleScroll);
    } else {
      document.removeEventListener('mouseover', handleScroll);
      resetInterval();
    }

    return () => {
      document.removeEventListener('mousedown', handleDeselect);
      document.removeEventListener('mousedown', handleSelectOnDrag);
      document.removeEventListener('mouseover', handleScroll);
      document.removeEventListener('mousemove', prepareDraggableStyle);
      document.removeEventListener('mouseup', onDrop);
    };
  }, [
    isDragging,
    isSelectioningOnDrag,
    prepareDraggableStyle,
    onDrop,
    handleScroll,
    handleSelectOnDrag,
    handleDeselect,
  ]);

  return {
    isDragging,
    isRequestPending,
    containersRefs,
    draggableOverlayRef,
    docListRef,
    setIsDragging,
    setIsRequestPending,
  };
}
